export const DEALER_LISTING_PHOTO_LIMITS = Object.freeze({
  maxFiles: 20,
  maxBytes: 8 * 1024 * 1024,
  allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  accept: "image/jpeg,image/png,image/webp",
  storedMaxDimension: 1400,
  storedQuality: 0.82,
});

/**
 * @param {File} file
 * @param {Record<string, string>} messages
 */
export function validateDealerListingPhotoFile(file, messages = {}) {
  if (!file || !file.size) {
    return { ok: false, message: messages.empty || "The file is empty." };
  }
  if (file.size > DEALER_LISTING_PHOTO_LIMITS.maxBytes) {
    return { ok: false, message: messages.tooLarge || "Each photo must be under 8 MB." };
  }
  if (!DEALER_LISTING_PHOTO_LIMITS.allowedMimeTypes.includes(file.type)) {
    return { ok: false, message: messages.invalidType || "Only JPG, PNG, or WebP images are allowed." };
  }
  return { ok: true };
}

/**
 * @param {File[]} files
 * @param {number} existingCount
 * @param {Record<string, string>} messages
 */
export function validateDealerListingPhotoSelection(files = [], existingCount = 0, messages = {}) {
  if (existingCount + files.length > DEALER_LISTING_PHOTO_LIMITS.maxFiles) {
    return { ok: false, message: messages.tooMany || `You can add up to ${DEALER_LISTING_PHOTO_LIMITS.maxFiles} photos.` };
  }

  for (const file of files) {
    const result = validateDealerListingPhotoFile(file, messages);
    if (!result.ok) return result;
  }

  return { ok: true, files };
}

async function loadImageFromFile(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Unable to read image."));
      element.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Resize and encode as a JPEG data URL for listing storage (no upload API required).
 * @param {File} file
 */
export async function createListingPhotoDataUrlFromFile(file) {
  const image = await loadImageFromFile(file);
  const maxDim = DEALER_LISTING_PHOTO_LIMITS.storedMaxDimension;
  const scale = Math.min(1, maxDim / Math.max(image.width, image.height, 1));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to process image.");
  }
  context.drawImage(image, 0, 0, width, height);
  const dataUrl = canvas.toDataURL("image/jpeg", DEALER_LISTING_PHOTO_LIMITS.storedQuality);
  if (!dataUrl.startsWith("data:image/")) {
    throw new Error("Unable to encode image.");
  }
  return dataUrl;
}
